import { motion } from 'framer-motion';
import { Zap, Layers, Palette } from 'lucide-react';

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
      delayChildren: 0.1,
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 30, scale: 0.98 },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: {
      duration: 0.5,
      ease: [0.16, 1, 0.3, 1] as const,
    },
  },
};

const manifestoCards = [
  {
    icon: Zap,
    title: 'Intuition over friction',
    description: 'Build by intent, not by wrestling tooling.',
    detail:
      "We're here to simplify app creation, break down technical barriers, and leave space for inspiration.",
  },
  {
    icon: Layers,
    title: 'An ecosystem',
    description: 'More than a builder, an evolving platform.',
    detail:
      'From creation to iteration to deployment: one coherent experience designed to move fast without compromise.',
  },
  {
    icon: Palette,
    title: 'Taste matters',
    description: 'Accattivante, elegant, consistent.',
    detail:
      'We obsess over clarity, craft, and feeling, because the best tools disappear and let you create.',
  },
];

export function ManifestoSection() {
  return (
    <section id="manifesto" className="py-24 lg:py-32 px-5 lg:px-12 bg-black scroll-mt-20">
      <div className="max-w-[1200px] mx-auto">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-100px' }}
          variants={containerVariants}
        >
          {/* Section Label */}
          <motion.p
            variants={itemVariants}
            className="text-xs font-medium text-white/40 uppercase tracking-[0.05em]"
          >
            Manifesto
          </motion.p>

          {/* Headline */}
          <motion.h2
            variants={itemVariants}
            className="mt-4 text-3xl sm:text-4xl lg:text-[40px] font-semibold leading-[1.2] tracking-[-0.01em] text-white max-w-2xl"
          >
            The future of building software is{' '}
            <span className="gradient-text">human creativity</span>, amplified.
          </motion.h2>

          {/* Description */}
          <motion.p
            variants={itemVariants}
            className="mt-6 text-base text-white/60 leading-relaxed max-w-2xl"
          >
            Michelangelo is a movement as much as a product: a network of people across the world,
            creating together, without the hard parts that used to slow us down.
          </motion.p>

          {/* Cards Grid */}
          <motion.div variants={containerVariants} className="mt-12 grid md:grid-cols-3 gap-4">
            {manifestoCards.map((card) => (
              <motion.div
                key={card.title}
                variants={itemVariants}
                className="glass-card glass-card-hover p-6 glass-card-blur"
                style={{ willChange: 'transform' }}
              >
                <card.icon className="w-5 h-5 text-white/70 mb-4" />
                <h3 className="text-base font-semibold text-white">{card.title}</h3>
                <p className="text-sm text-white/50 mt-1">{card.description}</p>
                <p className="text-sm text-white/40 mt-4 leading-relaxed">{card.detail}</p>
              </motion.div>
            ))}
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}
